import type { SectionType } from '@az3fr/core';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { InfoIcon } from 'lucide-react';
import { useLayoutEffect, useRef } from 'react';

import type { SectionTypeDisplayInfo } from '@/entities/section-plan/model/section-type';
import { sectionTypeDisplayInfosArray } from '@/entities/section-plan/model/section-type';

function ChooseSectionTypeViewV2(props: {
	onTypeSelected: (sectionType: SectionType) => void;
	onCancel: () => void;
}) {
	const { onTypeSelected, onCancel } = props;
	const containerRef = useRef<HTMLDivElement>(null);

	// Scroll into view when component mounts
	useLayoutEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest', // Only scroll if needed
				inline: 'nearest',
			});
		}
	}, []);

	function handleSectionTypeCardClick(sectionType: SectionType) {
		onTypeSelected(sectionType);
	}
	function handleSectionTypeCardKeyDown(sectionType: SectionType, e: React.KeyboardEvent) {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			onTypeSelected(sectionType);
		}
	}

	return (
		<div
			ref={containerRef}
			className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
		>
			{/* --- Header --- */}
			<div className="flex items-center justify-between border-gray-200 border-b bg-gray-50 px-5 py-4">
				<div className="flex items-center gap-3">
					<h3 className="font-bold text-gray-900 text-sm">Choose Section Type</h3>
				</div>
				<button
					type="button"
					onClick={onCancel}
					className="rounded-md border border-gray-200 bg-white px-3 py-1.5 font-medium text-gray-600 text-xs shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900"
				>
					Cancel
				</button>
			</div>

			{/* --- Section Type Cards --- */}
			<div className="p-5">
				<div className="grid grid-cols-2 items-stretch gap-3">
					{sectionTypeDisplayInfosArray.map((info, index) => {
						const isPrimary = index === 0;
						// Darker, richer borders and backgrounds
						const colorClass = isPrimary
							? 'border-blue-200 bg-blue-50/80 hover:border-blue-300 hover:bg-blue-100/50'
							: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50';

						return (
							<div key={info.coreModel.canonicalName} className={isPrimary ? 'col-span-2' : ''}>
								{/** biome-ignore lint/a11y/useSemanticElements: we have nested buttons */}
								<div
									role="button"
									tabIndex={0}
									onClick={() => handleSectionTypeCardClick(info.coreModel.canonicalName)}
									onKeyDown={(e) => handleSectionTypeCardKeyDown(info.coreModel.canonicalName, e)}
									className={`group relative h-full w-full cursor-pointer overflow-hidden rounded-lg border p-4 text-left transition-all hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/10 focus-visible:ring-offset-1 ${
										isPrimary ? `shadow-sm ${colorClass}` : `${colorClass}`
									}`}
								>
									<div className="relative">
										{/* Header Row: Title + Icon */}
										<div className="flex items-start justify-between gap-3">
											<div className="flex flex-col gap-1.5">
												{/* Recommended badge */}
												{isPrimary && (
													<span className="inline-flex w-fit rounded-full bg-blue-100 px-2 py-0.5 font-medium text-[0.65rem] text-blue-700 tracking-wide">
														Recommended
													</span>
												)}
												<h4 className="font-semibold text-gray-900 text-sm leading-tight transition-colors group-hover:text-gray-900">
													{info.displayName}
												</h4>
											</div>
											<div className="-mr-1 -mt-1 shrink-0">
												<SectionTypeInfoPopover sectionTypeDisplayInfo={info} />
											</div>
										</div>

										{/* Content Row: Description spans full width */}
										<p className="mt-2 pr-1 text-gray-500 text-xs leading-relaxed">
											{info.tagline}
										</p>
									</div>
								</div>
							</div>
						);
					})}
				</div>

				{/* --- Footer Info --- */}
				<div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
					<div className="flex items-start gap-2.5">
						<div className="mt-0.5 flex size-4 shrink-0 items-center justify-center">
							<svg
								aria-hidden="true"
								className="size-4 text-gray-500"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<circle cx="12" cy="12" r="10" />
								<line x1="12" y1="16" x2="12" y2="12" />
								<line x1="12" y1="8" x2="12.01" y2="8" />
							</svg>
						</div>
						<p className="text-gray-600 text-xs leading-relaxed">
							<span className="font-semibold text-gray-700">Standard</span> is the classic choice if
							you&apos;re unsure which type to use.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}

function SectionTypeInfoPopover(props: { sectionTypeDisplayInfo: SectionTypeDisplayInfo }) {
	const { sectionTypeDisplayInfo } = props;
	const { displayName, longDescription, examples } = sectionTypeDisplayInfo;

	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent card click
	};

	return (
		<Popover>
			<PopoverButton
				onClick={handleClick}
				className="flex size-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none sm:size-8"
			>
				<InfoIcon className="size-4 sm:size-4.5" />
			</PopoverButton>

			<PopoverPanel
				anchor="top"
				className="z-50 w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg [--anchor-gap:8px] [--anchor-padding:8px]"
			>
				<div className="space-y-4 p-4">
					{/* --- Popover Header --- */}
					<div className="flex items-center gap-2.5 border-gray-200 border-b pb-3">
						<div className="flex size-6 items-center justify-center rounded-full bg-gray-100 sm:size-7">
							<svg
								aria-hidden="true"
								className="size-3 text-gray-600 sm:size-4"
								viewBox="0 0 12 12"
								fill="none"
							>
								<path d="M6 9L3 6L4.5 4.5L6 6L7.5 4.5L9 6L6 9Z" fill="currentColor" />
							</svg>
						</div>
						<h4 className="font-semibold text-gray-900 text-sm sm:text-base">{displayName}</h4>
					</div>

					<p className="text-gray-700 text-sm leading-relaxed">{longDescription}</p>

					{/* --- Examples --- */}
					{examples.length > 0 && (
						<div className="space-y-3">
							<div className="flex items-center gap-2">
								<h5 className="font-medium text-gray-500 text-xs uppercase tracking-widest">
									Examples
								</h5>
								<div className="h-px flex-1 bg-gray-200" />
							</div>
							<div className="space-y-2">
								{examples.map((example, index) => (
									<div
										key={example}
										className="relative rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-800 text-sm leading-relaxed"
									>
										<div className="flex items-start gap-2">
											<div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-gray-200 font-semibold text-gray-600 text-xs">
												{index + 1}
											</div>
											<span className="italic">{example}</span>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</PopoverPanel>
		</Popover>
	);
}

export { ChooseSectionTypeViewV2 };
